import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  useIsMobile,
} from "@devhop/ui";
import { Link, useParams, useRouterState } from "@tanstack/react-router";
import { Fragment } from "react";
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/ui/breadcrumb";
import { useBreadcrumbLabelContext } from "./breadcrumbs/BreadcrumbLabelContext";
import {
  HOME_ROUTE,
  resolveBreadcrumbTrail,
} from "./breadcrumbs/resolve-trail";

export function Breadcrumbs() {
  const isMobile = useIsMobile();
  const params = useParams({ strict: false });
  const { lastLabelOverride } = useBreadcrumbLabelContext() ?? {};

  const routeId = useRouterState({
    select: (s) => {
      const leaf = s.matches.at(-1);
      return (leaf?.routeId ?? leaf?.fullPath ?? s.location.pathname) as string;
    },
  });

  const crumbs = resolveBreadcrumbTrail(
    routeId,
    params,
    lastLabelOverride,
  );

  if (crumbs.length === 0) return null;

  const first = crumbs[0];
  const last = crumbs[crumbs.length - 1];
  const middle = crumbs.slice(1, -1);
  const collapse = isMobile && crumbs.length > 2 && first && last;

  return (
    <Breadcrumb className="min-w-0 flex-1">
      <BreadcrumbList className="flex-nowrap">
        {collapse ? (
          <>
            <BreadcrumbItem>
              {first.to ? (
                <BreadcrumbLink asChild>
                  <Link to={first.to} params={first.params}>
                    {first.label}
                  </Link>
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage>{first.label}</BreadcrumbPage>
              )}
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            {middle.length > 0 ? (
              <>
                <BreadcrumbItem>
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      className="flex items-center gap-1"
                      aria-label="ສະແດງເສັ້ນທາງເພີ່ມເຕີມ"
                    >
                      <BreadcrumbEllipsis />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {middle.map((crumb) => (
                        <DropdownMenuItem key={crumb.label} asChild>
                          {crumb.to ? (
                            <Link to={crumb.to} params={crumb.params}>
                              {crumb.label}
                            </Link>
                          ) : (
                            <span>{crumb.label}</span>
                          )}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
              </>
            ) : null}
            <BreadcrumbItem className="min-w-0">
              <BreadcrumbPage className="max-w-[45vw] truncate">
                {last.label}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </>
        ) : (
          crumbs.map((crumb, index) => {
            const isLast = index === crumbs.length - 1;
            return (
              <Fragment key={`${crumb.label}-${index}`}>
                <BreadcrumbItem className={isLast ? "min-w-0" : undefined}>
                  {isLast || !crumb.to ? (
                    <BreadcrumbPage className="max-w-[50vw] truncate sm:max-w-none">
                      {crumb.label}
                    </BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link to={crumb.to} params={crumb.params}>
                        {crumb.label}
                      </Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {isLast ? null : <BreadcrumbSeparator />}
              </Fragment>
            );
          })
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

export { HOME_ROUTE };
